import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import type { SheetData, CellData, CellFormat } from './types';

interface UseXlsxParserResult {
  sheets: SheetData[];
  isLoading: boolean;
  error: string | null;
}

// Color type for xlsx cell styles (not exported by xlsx types)
interface XlsxColor {
  rgb?: string;
  theme?: number;
  tint?: number;
}

function getCellRef(row: number, col: number): string {
  let colStr = '';
  let c = col;
  while (c >= 0) {
    colStr = String.fromCharCode((c % 26) + 65) + colStr;
    c = Math.floor(c / 26) - 1;
  }
  return `${colStr}${row + 1}`;
}

function parseColor(color: XlsxColor | undefined): string | undefined {
  if (!color) return undefined;
  if (color.rgb) {
    // xlsx returns RGB without # prefix, and sometimes with alpha (ARGB)
    const rgb = color.rgb.length === 8 ? color.rgb.slice(2) : color.rgb;
    return `#${rgb}`;
  }
  return undefined;
}

function extractCellFormat(
  cell: XLSX.CellObject | undefined,
): CellFormat | undefined {
  if (!cell?.s) return undefined;

  const style = cell.s;

  const format: CellFormat = {};
  let hasFormat = false;

  if (style.font?.bold) {
    format.bold = true;
    hasFormat = true;
  }
  if (style.font?.italic) {
    format.italic = true;
    hasFormat = true;
  }
  if (style.font?.color) {
    const textColor = parseColor(style.font.color);
    if (textColor && textColor !== '#000000') {
      format.textColor = textColor;
      hasFormat = true;
    }
  }
  if (style.fill?.fgColor) {
    const bgColor = parseColor(style.fill.fgColor);
    if (bgColor && bgColor !== '#FFFFFF' && bgColor !== '#ffffff') {
      format.bgColor = bgColor;
      hasFormat = true;
    }
  }

  return hasFormat ? format : undefined;
}

function getCellType(cell: XLSX.CellObject | undefined): CellData['type'] {
  if (!cell) return 'empty';

  switch (cell.t) {
    case 'n':
      return 'number';
    case 'd':
      return 'date';
    case 'b':
      return 'boolean';
    case 's':
      return 'string';
    default:
      return 'string';
  }
}

function getCellValue(
  cell: XLSX.CellObject | undefined,
): string | number | null {
  if (!cell) return null;

  // Use formatted value if available, otherwise raw value
  if (cell.w !== undefined) return String(cell.w);
  if (cell.v === undefined || cell.v === null) return null;

  // Format dates nicely
  if (cell.t === 'd' && cell.v instanceof Date) {
    return cell.v.toLocaleDateString();
  }

  if (typeof cell.v === 'number') return cell.v;
  if (typeof cell.v === 'string') return cell.v;
  if (typeof cell.v === 'boolean') return cell.v ? 'TRUE' : 'FALSE';

  return String(cell.v);
}

function parseWorksheet(
  worksheet: XLSX.WorkSheet,
  sheetName: string,
): SheetData {
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const numCols = range.e.c - range.s.c + 1;
  const numRows = range.e.r - range.s.r + 1;

  // Extract headers from first row
  const headers: string[] = [];
  for (let col = 0; col < numCols; col++) {
    const cellRef = getCellRef(range.s.r, range.s.c + col);
    const cell = worksheet[cellRef];
    headers.push(cell?.v?.toString() || XLSX.utils.encode_col(col));
  }

  // Extract data rows (skip header row)
  const rows: CellData[][] = [];
  for (let row = 1; row < numRows; row++) {
    const rowData: CellData[] = [];
    let hasData = false;

    for (let col = 0; col < numCols; col++) {
      const cellRef = getCellRef(range.s.r + row, range.s.c + col);
      const cell = worksheet[cellRef];
      const value = getCellValue(cell);

      if (value !== null && value !== '') {
        hasData = true;
      }

      rowData.push({
        value,
        type: getCellType(cell),
        format: extractCellFormat(cell),
      });
    }

    // Skip completely empty rows
    if (hasData) {
      rows.push(rowData);
    }
  }

  const excelColWidths = Array.isArray(worksheet['!cols'])
    ? worksheet['!cols']
    : undefined;

  const columnWidths = headers.map((header, colIndex) => {
    const excelCol = excelColWidths?.[colIndex];
    if (excelCol?.wpx) {
      return Math.min(400, Math.max(60, excelCol.wpx));
    }
    if (excelCol?.wch) {
      return Math.min(400, Math.max(60, excelCol.wch * 8));
    }

    let maxLen = header.length;
    for (const row of rows.slice(0, 100)) {
      const cellValue = row[colIndex]?.value;
      if (cellValue !== null) {
        maxLen = Math.max(maxLen, String(cellValue).length);
      }
    }
    return Math.min(300, Math.max(60, maxLen * 7 + 16));
  });

  const expandedColumnWidths = headers.map((header, colIndex) => {
    let maxLen = header.length;
    for (const row of rows) {
      const cellValue = row[colIndex]?.value;
      if (cellValue !== null) {
        maxLen = Math.max(maxLen, String(cellValue).length);
      }
    }
    return Math.max(60, maxLen * 7 + 16);
  });

  return {
    name: sheetName,
    headers,
    rows,
    columnWidths,
    expandedColumnWidths,
  };
}

export function useXlsxParser(url: string): UseXlsxParserResult {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setSheets([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    let isCancelled = false;

    async function fetchAndParse() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(url, { signal: abortController.signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        if (isCancelled || abortController.signal.aborted) return;

        const workbook = XLSX.read(arrayBuffer, {
          type: 'array',
          cellStyles: true,
          cellDates: true,
        });

        const parsedSheets: SheetData[] = workbook.SheetNames.map((sheetName) =>
          parseWorksheet(workbook.Sheets[sheetName], sheetName),
        );

        if (isCancelled || abortController.signal.aborted) return;
        setSheets(parsedSheets);
      } catch (err) {
        if (isCancelled || abortController.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;

        console.error('Error parsing xlsx:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to parse Excel file',
        );
      } finally {
        if (!isCancelled && !abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    void fetchAndParse();

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [url]);

  return { sheets, isLoading, error };
}
